import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Flag, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MessageSquare,
  FileText,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'action_taken', label: 'Action Taken' }
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'post', label: 'Posts' },
  { value: 'message', label: 'Messages' },
  { value: 'user', label: 'Users' }
];

const getTypeIcon = (type) => {
  const icons = { post: FileText, message: MessageSquare, user: User };
  return icons[type] || Flag;
};

const getStatusBadge = (status) => {
  const badges = {
    pending: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
    reviewed: { color: 'bg-blue-100 text-blue-700', icon: Eye },
    dismissed: { color: 'bg-slate-100 text-slate-700', icon: XCircle },
    action_taken: { color: 'bg-green-100 text-green-700', icon: CheckCircle }
  };
  return badges[status] || badges.pending;
};

export default function AdminReportsPage({ admin }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewingReport, setReviewingReport] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchReports();
  }, [page, statusFilter, typeFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('content_type', typeFilter);

      const response = await axios.get(`${API}/admin/reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReports(response.data.reports);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (reportId, status) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.put(
        `${API}/admin/reports/${reportId}?status=${status}${reviewNotes ? `&notes=${encodeURIComponent(reviewNotes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Report reviewed');
      setReviewingReport(null);
      setReviewNotes('');
      fetchReports();
    } catch (error) {
      toast.error('Failed to review report');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reported Content</h1>
          <p className="text-slate-500">{total} reports</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white"
        >
          {STATUS_OPTIONS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white"
        >
          {TYPE_OPTIONS.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
          <p className="text-slate-500">No reports to review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => {
            const TypeIcon = getTypeIcon(report.content_type);
            const statusBadge = getStatusBadge(report.status);
            const StatusIcon = statusBadge.icon;

            return (
              <div
                key={report.id}
                className="bg-white rounded-xl border border-slate-200 p-5"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <TypeIcon className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 capitalize">
                          {report.content_type} Report
                        </span>
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusBadge.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {report.status}
                        </span>
                      </div>
                      <p className="text-slate-600 mt-1">{report.reason}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        Reported on {new Date(report.created_at).toLocaleString('en-IN')}
                      </p>
                      {report.review_notes && (
                        <p className="text-sm text-slate-500 mt-2 italic">
                          Notes: {report.review_notes}
                        </p>
                      )}
                    </div>
                  </div>

                  {report.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setReviewingReport(report)}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm"
                      >
                        Review
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewingReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Review Report</h2>
            <p className="text-slate-600 mb-4">{reviewingReport.reason}</p>

            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Add review notes (optional)..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              rows={3}
            />

            <div className="flex gap-2">
              <button
                onClick={() => handleReview(reviewingReport.id, 'action_taken')}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
              >
                Take Action
              </button>
              <button
                onClick={() => handleReview(reviewingReport.id, 'dismissed')}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 text-sm"
              >
                Dismiss
              </button>
              <button
                onClick={() => setReviewingReport(null)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
